import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../../api/contexto'
import type { ErrorApi } from '../../api/errores'
import type { components } from '../../api/modelos'
import { TAMANO_PAGINA } from '../../componentes/paginacion'

export type Canal = components['schemas']['Canal']

/**
 * `meta` es un diccionario abierto en el backend (`dict[str, Any]`), y de eso
 * openapi-typescript saca `Record<string, never>`: un tipo que dice "objeto sin
 * ninguna propiedad util". Es un artefacto del generador, no el contrato. Se
 * corrige aqui, en un solo sitio, en vez de repartir `as` por los componentes.
 */
export type Metadatos = Record<string, unknown>

type SesionGenerada = components['schemas']['SesionResumen']
type MensajeGenerado = components['schemas']['Mensaje']
type PaginaGenerada = components['schemas']['Pagina_SesionResumen_']

export type SesionResumen = Omit<SesionGenerada, 'meta'> & { meta?: Metadatos | null }
export type Mensaje = Omit<MensajeGenerado, 'meta'> & { meta?: Metadatos | null }
export type PaginaSesiones = Omit<PaginaGenerada, 'items'> & { items: SesionResumen[] }

/**
 * El listado visible pide 15, como todos. La apertura directa por teléfono
 * (`?buscar=`, desde el perfil 360) busca aparte en una tanda de 200, el máximo
 * que acepta el backend: con 15 casi nunca estaría en la página que se ve.
 */
export const TAMANO_BUSQUEDA = 200

/** El sondeo de la conversacion abierta. */
export const INTERVALO_SONDEO = 12_000

/**
 * Codifica un identificador de sesion para meterlo en un segmento de ruta.
 *
 * En clientes el identificador es el nombre de perfil de WhatsApp: `Angie`,
 * `Administración Obras Maestrat`, `🧔🏻`, `❤️`, y **un unico punto**.
 * `encodeURIComponent` deja el punto sin tocar, y un segmento que sea
 * exactamente `.` o `..` lo colapsa la norma de resolucion de rutas antes de
 * que la peticion salga del cliente. Por eso el punto se escapa tambien, tal y
 * como documenta el parametro en `/docs`.
 *
 * ⚠️ Medido: el analizador de URL de los navegadores (y el de `fetch` en Node)
 * trata `%2E` a solas como un segmento de un punto y lo colapsa igualmente, asi
 * que la sesion llamada exactamente `.` no es alcanzable por ruta desde un
 * navegador, escapada o no. La codificacion correcta es esta; el hueco esta en
 * el contrato del endpoint, que necesitaria aceptar el identificador fuera de
 * la ruta. Cualquier otro nombre, punto incluido (`Ana.M`), viaja bien.
 */
export function codificarSesion(sid: string): string {
  return encodeURIComponent(sid).replace(/\./g, '%2E')
}

function rutaSesiones(canal: Canal): string {
  return `/api/conversaciones/${canal}/sesiones`
}

export function useSesiones(canal: Canal, page: number, tamano: number = TAMANO_PAGINA, activo = true) {
  const api = useApi()

  return useQuery<PaginaSesiones, ErrorApi>({
    enabled: activo,
    queryKey: ['sesiones', canal, page, tamano],
    queryFn: ({ signal }) =>
      api.pedir<PaginaSesiones>(rutaSesiones(canal), {
        parametros: { page, page_size: tamano },
        senal: signal,
      }),
  })
}

/**
 * Une lo que ya se tenia con lo que ha traido el sondeo. Devuelve el array
 * anterior sin tocar cuando no hay novedades: asi la referencia no cambia,
 * React no vuelve a pintar 250 burbujas cada 12 segundos y la posicion del
 * scroll se queda donde el operador la dejo.
 */
function unir(previos: Mensaje[], nuevos: Mensaje[]): Mensaje[] {
  if (nuevos.length === 0) return previos
  const conocidos = new Set(previos.map((mensaje) => mensaje.id))
  const extra = nuevos.filter((mensaje) => !conocidos.has(mensaje.id))
  return extra.length === 0 ? previos : [...previos, ...extra]
}

/**
 * Los mensajes de una conversacion, con sondeo incremental.
 *
 * Solo sondea la conversacion abierta (`enabled`), y solo con la pestana
 * delante (`refetchIntervalInBackground: false`). Cada vuelta manda el `id` del
 * ultimo mensaje conocido como cursor `desde`, de modo que la conversacion de
 * 250 mensajes se descarga una vez y a partir de ahi solo viaja lo nuevo.
 */
export function useMensajes(canal: Canal, sesion: string | null) {
  const api = useApi()
  const consultas = useQueryClient()
  const clave = ['mensajes', canal, sesion] as const

  return useQuery<Mensaje[], ErrorApi>({
    queryKey: clave,
    enabled: sesion !== null,
    queryFn: async ({ signal }) => {
      // No puede ocurrir con `enabled`, pero el tipo no lo sabe y una guarda
      // honesta es mejor que una asercion.
      if (sesion === null) return []

      const previos = consultas.getQueryData<Mensaje[]>(clave) ?? []
      const desde = previos.at(-1)?.id
      const ruta = `${rutaSesiones(canal)}/${codificarSesion(sesion)}/mensajes`
      const nuevos = await api.pedir<Mensaje[]>(ruta, {
        parametros: { desde },
        senal: signal,
      })

      return desde === undefined ? nuevos : unir(previos, nuevos)
    },
    refetchInterval: INTERVALO_SONDEO,
    refetchIntervalInBackground: false,
    // El sondeo ya es la fuente de frescura: sin esto, volver a la pestana
    // dispararia ademas una recarga completa de la conversacion.
    staleTime: INTERVALO_SONDEO,
  })
}
